import React from 'react';

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizeText(text: string) {
  const normalized = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return escapeRegExp(normalized);
}

export type HighlightProps = {
  /** Can be a single string or multiple words separated by spaces */
  query: string;
  children: string;
  className?: {
    root?: string;
    highlight?: string;
  };
};

export function Highlight({ query, children, className }: HighlightProps) {
  if (!query || !children) {
    return <span className={className?.root}>{children}</span>;
  }

  // 1. Split on whitespace, trim, normalize and escape each term
  const terms = query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(term => normalizeText(term));

  if (terms.length === 0) {
    return <span className={className?.root}>{children}</span>;
  }

  // 2. Build a single regex matching ANY of the terms
  const regex = new RegExp(`(${terms.join('|')})`, 'gi');

  // 3. We'll walk through the normalized children string, but render the original text
  const normalizedChildren = normalizeText(children);
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  // 4. Split on the regex; split keeps the delimiters when you wrap them in parentheses
  normalizedChildren.split(regex).forEach((part, index) => {
    const length = part.length;
    const original = children.substring(cursor, cursor + length);

    if (terms.some(t => t && part.toLowerCase() === t.toLowerCase())) {
      // Highlight matches
      parts.push(
        <span
          key={index}
          className={['ea-highlight font-bold', className?.highlight].filter(Boolean).join(' ')}
        >
          {original}
        </span>,
      );
    } else {
      // Non‐matching text
      parts.push(<span key={index}>{original}</span>);
    }

    cursor += length;
  });

  return <span className={className?.root}>{parts}</span>;
}
